import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdBook } from "react-icons/md";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import { NotifyContext } from "../../context/notifyContext";
import { PaginationType } from "../../Types/APITypes";
import { Course } from "../../Types/Courses";
import CourseService from "../../services/api/CourseService";
import Table from "../../UI/Table/Table";
import Button from "../../UI/Button/Button";

export default function CourseList() {
  const navigate = useNavigate();
  const notify = useContext(NotifyContext);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [tableInfos, setTableInfos] = useState({ pageCount: 1, total: 0 });

  useBreadcrumbs([
    { label: "Tableau de bord", is_link: true, href: "/app/dashboard" },
    { label: "Cours", icon: <MdBook />, is_link: true, href: "/app/courses" },
    { label: "Liste", is_link: false },
  ]);

  const fetchData = useCallback(
    async (pagination?: PaginationType) => {
      setLoading(true);
      try {
        const response: any = await CourseService.getCourses({
          page: (pagination?.current_page || 0) + 1,
          per_page: pagination?.per_page || 20,
          search_word: pagination?.search_word,
        });
        const data = response?.data;
        setCourses(data?.courses || []);
        setTableInfos({
          pageCount: data?.last_page || 1,
          total: data?.total || 0,
        });
      } catch (error: any) {
        notify(error?.message || error || "Erreur lors du chargement des cours", "error");
      } finally {
        setLoading(false);
      }
    },
    [notify]
  );

  const handleDelete = async (course: Course) => {
    if (!window.confirm(`Supprimer le cours "${course.title}" ?`)) return;
    try {
      await CourseService.deleteCourse(course.id);
      notify("Cours supprimé avec succès");
      fetchData({ current_page: 0, per_page: 20 });
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors de la suppression", "error");
    }
  };

  const columns = useMemo(
    () => [
      { Header: "Titre", accessor: "title" },
      {
        Header: "Catégorie",
        Cell: ({ row }: any) => row.original.category || "-",
      },
      {
        Header: "Publication",
        Cell: ({ row }: any) => (
          <span
            className={`inline-flex rounded px-2 py-1 text-xs ${
              row.original.is_published
                ? "bg-green-100 text-green-700"
                : "bg-yellow-100 text-yellow-700"
            }`}
          >
            {row.original.is_published ? "Publié" : "Brouillon"}
          </span>
        ),
      },
      {
        Header: "Ordre",
        accessor: "order",
      },
      {
        Header: "Actions",
        Cell: ({ row }: any) => {
          const course: Course = row.original;
          return (
            <div className="flex items-center gap-2">
              <Button onClick={() => navigate(`/app/courses/${course.id}/details`)}>
                Détails
              </Button>
              <Button color="info" onClick={() => navigate(`/app/courses/${course.id}/edit`)}>
                Éditer
              </Button>
              <Button color="error" onClick={() => handleDelete(course)}>
                Supprimer
              </Button>
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button color="primary" onClick={() => navigate("/app/courses/create")}>
          Nouveau cours
        </Button>
      </div>

      {tableInfos.total === 0 && !loading ? (
        <div className="border border-dashed border-black/40 rounded p-8 text-center">
          Aucun cours disponible. Crée ton premier cours.
        </div>
      ) : null}

      <Table
        // @ts-ignore
        columns={columns}
        data={courses}
        fetchData={fetchData}
        loading={loading}
        pageCount={tableInfos.pageCount}
        total={tableInfos.total}
      />
    </div>
  );
}
